import { useState, useCallback, useMemo } from 'react'
import { ILvl4Item } from 'pages/NewProject/components/ProjectSkillsStandards/interfaces'
import {
  BrowseTab,
  BrowseTreeData,
  NavigationPathItem,
  TreeItem,
  getChildren,
} from '../types'

/**
 * Returns the minimum grade sequence found within an item or any of its descendants.
 * Used to sort tree items at any level by grade (Kindergarten first, then Grade 1, ...).
 */
const getMinGradeSequence = (item: TreeItem): number => {
  const directGrades = (item as ILvl4Item).grades
  let min = directGrades?.length
    ? Math.min(...directGrades.map(g => g.sequence ?? Infinity))
    : Infinity

  const children = getChildren(item)
  for (const child of children) {
    const childMin = getMinGradeSequence(child)
    if (childMin < min) min = childMin
  }
  return min
}

const getSortCode = (item: TreeItem): string => {
  const lvl4 = item as ILvl4Item
  return lvl4.originalCodes ?? lvl4.original_codes ?? item.name ?? ''
}

/**
 * Sorts tree items by Grade (min sequence) first, then by Code/name.
 * Applied at every level so Kindergarten branches come before Grade 1 branches.
 */
const sortTreeItems = (items: TreeItem[]): TreeItem[] => {
  if (items.length === 0) return items

  return [...items].sort((a, b) => {
    const aSeq = getMinGradeSequence(a)
    const bSeq = getMinGradeSequence(b)

    if (aSeq !== bSeq) return aSeq - bSeq

    return getSortCode(a).localeCompare(getSortCode(b), undefined, { numeric: true })
  })
}

export interface UseBrowseNavigationResult {
  activeTab: BrowseTab
  setActiveTab: (tab: BrowseTab) => void
  currentLevel: 1 | 2 | 3 | 4
  navigationPath: NavigationPathItem[]
  currentItems: TreeItem[]
  navigateForward: (item: TreeItem) => void
  navigateBack: () => void
  navigateToBreadcrumb: (level: number) => void
  resetNavigation: () => void
}

export const useBrowseNavigation = (
  data: BrowseTreeData | null,
  isDataReady: boolean,
): UseBrowseNavigationResult => {
  const [activeTab, setActiveTabState] = useState<BrowseTab>('standards')
  const [currentLevel, setCurrentLevel] = useState<1 | 2 | 3 | 4>(1)
  const [navigationPath, setNavigationPath] = useState<NavigationPathItem[]>([])

  const rootItems = useMemo((): TreeItem[] => {
    if (!data) return []
    const raw = activeTab === 'standards' ? (data.subjects ?? []) : (data.axes ?? [])
    return sortTreeItems(raw)
  }, [data, activeTab])

  const currentItems = useMemo((): TreeItem[] => {
    if (!isDataReady || rootItems.length === 0) return []
    if (navigationPath.length === 0) return rootItems

    let items: TreeItem[] = rootItems
    for (const pathItem of navigationPath) {
      const found = items.find(i => i.id === pathItem.id)
      if (!found) return []
      items = getChildren(found)
    }

    return sortTreeItems(items)
  }, [isDataReady, rootItems, navigationPath])

  const navigateForward = useCallback((item: TreeItem) => {
    if (!isDataReady || currentLevel >= 4) return

    const children = getChildren(item)
    if (children.length === 0) return

    const newPathItem: NavigationPathItem = {
      id: item.id,
      name: item.name,
      level: currentLevel as 1 | 2 | 3,
      color: 'color' in item ? item.color : undefined,
    }

    setNavigationPath(prev => [...prev, newPathItem])
    setCurrentLevel(prev => (prev + 1) as 1 | 2 | 3 | 4)
  }, [isDataReady, currentLevel])

  const navigateBack = useCallback(() => {
    if (currentLevel <= 1) return
    setNavigationPath(prev => prev.slice(0, -1))
    setCurrentLevel(prev => (prev - 1) as 1 | 2 | 3 | 4)
  }, [currentLevel])

  const navigateToBreadcrumb = useCallback((level: number) => {
    const validLevel = Math.max(1, Math.min(4, Math.floor(level))) as 1 | 2 | 3 | 4
    if (validLevel >= currentLevel) return
    setNavigationPath(prev => prev.slice(0, validLevel - 1))
    setCurrentLevel(validLevel)
  }, [currentLevel])

  const resetNavigation = useCallback(() => {
    setNavigationPath([])
    setCurrentLevel(1)
  }, [])

  const setActiveTab = useCallback((tab: BrowseTab) => {
    setActiveTabState(tab)
    setNavigationPath([])
    setCurrentLevel(1)
  }, [])

  return useMemo(() => ({
    activeTab,
    setActiveTab,
    currentLevel,
    navigationPath,
    currentItems,
    navigateForward,
    navigateBack,
    navigateToBreadcrumb,
    resetNavigation,
  }), [
    activeTab,
    setActiveTab,
    currentLevel,
    navigationPath,
    currentItems,
    navigateForward,
    navigateBack,
    navigateToBreadcrumb,
    resetNavigation,
  ])
}
